import { SNOOZE_MS, STORAGE_KEY } from './constants';
import { buildAlerts, monitorVerdictIdentity, needsAttention } from './utils';

const readSeen = () => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : null;
        const seen = parsed?.seen;
        if (!seen || typeof seen !== 'object' || Array.isArray(seen)) return {};
        return Object.fromEntries(
            Object.entries(seen).filter(([, identity]) => Number.isSafeInteger(identity) && identity >= 0)
        );
    } catch {
        return {};
    }
};

const writeSeen = seen => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ seen }));
    } catch {
        // Storage restrictions must not prevent alerts from working in this tab.
    }
};

const snoozeKey = monitor =>
    `${STORAGE_KEY}:snooze:${monitor.organization_id}:${monitor.owner_id}:${monitor.monitor_id}`;

const readSnooze = monitor => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(snoozeKey(monitor)));
        return stored?.identity === monitorVerdictIdentity(monitor) && Number.isFinite(stored.until) ? stored.until : 0;
    } catch {
        return 0;
    }
};

const writeSnooze = (monitor, until) => {
    try {
        window.localStorage.setItem(
            snoozeKey(monitor),
            JSON.stringify({ identity: monitorVerdictIdentity(monitor), until })
        );
    } catch {
        // Keep the in-memory snooze when storage is unavailable.
    }
};

const INITIAL_STATE = {
    monitors: null,
    seen: {},
    // Retain identities across an empty poll so returning results are not announced again.
    resultIdentities: {},
    // Dismissed arrivals stay tracked so the banner does not present them as missed.
    arrivals: [],
    snoozedUntil: {},
    hasBaseline: false,
    bannerDismissedFor: {},
    runningToastHidden: false,
    stopped: false,
};

let state = { ...INITIAL_STATE, seen: readSeen() };
const listeners = new Set();

const emit = next => {
    state = next;
    listeners.forEach(listener => listener());
};

// Merge before writing so an older tab cannot replace a newer read version.
const mergeSeen = (extra = {}) => {
    const merged = { ...state.seen };
    [readSeen(), extra].forEach(markers => {
        Object.entries(markers).forEach(([monitorId, version]) => {
            merged[monitorId] = Math.max(merged[monitorId] ?? -1, version);
        });
    });
    return merged;
};

export const subscribe = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getState = () => state;

export const setMonitors = incoming => {
    if (!Array.isArray(incoming)) return;
    const monitors = incoming;
    const live = new Set(monitors.map(monitor => monitor.monitor_id));

    const observed = buildAlerts(monitors, state.seen);

    // The first nonempty feed is the missed backlog; later changes are live arrivals.
    const justArrived = state.hasBaseline
        ? observed
              .filter(({ monitorId, identity }) => state.resultIdentities[monitorId] !== identity)
              .map(({ monitorId, identity }) => ({ monitorId, identity }))
        : [];

    const resultIdentities = { ...state.resultIdentities };
    monitors.forEach(monitor => {
        if (monitor.latest_verdict) resultIdentities[monitor.monitor_id] = monitorVerdictIdentity(monitor);
        else delete resultIdentities[monitor.monitor_id];
    });

    const replaced = new Set(justArrived.map(entry => entry.monitorId));
    const stillListed = entry => !monitors.length || live.has(entry.monitorId);
    const arrivals = [
        ...justArrived,
        ...state.arrivals.filter(entry => !replaced.has(entry.monitorId) && stillListed(entry)),
    ];
    const seen = mergeSeen();
    if (JSON.stringify(seen) !== JSON.stringify(state.seen)) writeSeen(seen);
    const previousMonitors = new Map((state.monitors ?? []).map(monitor => [monitor.monitor_id, monitor]));
    const snoozedUntil = {};
    monitors.forEach(monitor => {
        const previous = previousMonitors.get(monitor.monitor_id);
        const sameResult =
            previous &&
            snoozeKey(previous) === snoozeKey(monitor) &&
            monitorVerdictIdentity(previous) === monitorVerdictIdentity(monitor);
        const until = Math.max(readSnooze(monitor), sameResult ? state.snoozedUntil[monitor.monitor_id] ?? 0 : 0);
        if (until > Date.now()) {
            snoozedUntil[monitor.monitor_id] = until;
            if (!arrivals.some(entry => entry.monitorId === monitor.monitor_id)) {
                arrivals.push({ monitorId: monitor.monitor_id, identity: monitorVerdictIdentity(monitor) });
            }
        }
    });

    emit({
        ...state,
        monitors,
        seen,
        resultIdentities,
        arrivals,
        snoozedUntil,
        hasBaseline: state.hasBaseline || monitors.length > 0,
    });
};

export const stop = () => emit({ ...state, stopped: true });

const withDismissed = (arrivals, monitorId = null) =>
    arrivals.map(entry => (monitorId == null || entry.monitorId === monitorId ? { ...entry, dismissed: true } : entry));

const withTrackedAlert = (arrivals, monitorId) => {
    if (arrivals.some(entry => entry.monitorId === monitorId)) return arrivals;
    const monitor = (state.monitors ?? []).find(candidate => candidate.monitor_id === monitorId);
    if (!monitor?.latest_verdict) return arrivals;
    return [...arrivals, { monitorId, identity: monitorVerdictIdentity(monitor) }];
};

const attentionBacklog = () =>
    Object.fromEntries(
        buildAlerts(state.monitors, state.seen)
            .filter(alert => alert.isUnread && needsAttention(alert))
            .map(alert => [alert.monitorId, alert.identity])
    );

export const markAllRead = () => {
    const bannerDismissedFor = attentionBacklog();
    const stamps = {};
    (state.monitors ?? []).forEach(monitor => {
        if (monitor.latest_verdict) stamps[monitor.monitor_id] = monitorVerdictIdentity(monitor);
    });
    const seen = mergeSeen(stamps);
    writeSeen(seen);
    emit({ ...state, seen, arrivals: withDismissed(state.arrivals), bannerDismissedFor });
};

export const markRead = monitorId => {
    const monitor = (state.monitors ?? []).find(candidate => candidate.monitor_id === monitorId);
    if (!monitor?.latest_verdict) return;
    const seen = mergeSeen({ [monitorId]: monitorVerdictIdentity(monitor) });
    writeSeen(seen);
    emit({ ...state, seen, arrivals: withDismissed(state.arrivals, monitorId) });
};

export const dismissAlerts = monitorIds => {
    const ids = new Set(monitorIds);
    const arrivals = monitorIds.reduce(withTrackedAlert, state.arrivals);
    emit({
        ...state,
        arrivals: arrivals.map(entry => (ids.has(entry.monitorId) ? { ...entry, dismissed: true } : entry)),
    });
};

export const dismissArrival = monitorId => dismissAlerts([monitorId]);

export const dismissAllArrivals = () => emit({ ...state, arrivals: withDismissed(state.arrivals) });

export const snoozeArrival = (monitorId, now) => {
    const monitor = (state.monitors ?? []).find(candidate => candidate.monitor_id === monitorId);
    if (!monitor?.latest_verdict) return;
    writeSnooze(monitor, now + SNOOZE_MS);
    emit({
        ...state,
        arrivals: withTrackedAlert(state.arrivals, monitorId),
        snoozedUntil: { ...state.snoozedUntil, [monitorId]: now + SNOOZE_MS },
    });
};

export const dismissBanner = () => emit({ ...state, bannerDismissedFor: attentionBacklog() });

export const hideRunningToast = () => emit({ ...state, runningToastHidden: true });

export const showRunningToast = () => emit({ ...state, runningToastHidden: false });

export const resetForTests = () => {
    state = { ...INITIAL_STATE, seen: {} };
    listeners.forEach(listener => listener());
};
